import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sheet, Plus, RefreshCw, Eye, Send, CheckCircle2, XCircle, RotateCcw, Printer, Trash, ChevronRight, BookOpen } from 'lucide-react';
import { toast } from 'react-toastify';
import { PAYROLL_STATUS_LABELS } from '../payroll/core/domain';
import { approvePeriod, deletePeriod, listPeriods, listTemplates, rejectPeriod, reopenPeriod, submitPeriod } from '../payroll/data/periodActions';
import { PAYROLL_PATHS, canManagePayroll, canViewPayrollPage, currentPayrollUser } from '../payroll/data/permissions';
import { ConfirmWarningsModal, ReasonModal } from '../payroll/components/PeriodDialogs';
import {
    AccessDenied, Badge, ConfirmDialog, EmptyState, FilterField, IconAction, ListToolbar, PageShell, Pagination, SortableTh, Spinner,
    StatCards, buttonPrimary, buttonSecondary, formatDateTime, formatMoney, inputClass, matchesSearch, tableClass, tbodyClass, tdClass, thClass,
    theadClass, usePagination, useSortedRows,
} from '../payroll/components/ui';

export const STATUS_TONES = { NHAP: 'gray', CHO_DUYET: 'yellow', TU_CHOI: 'red', DA_DUYET: 'green' };

const BangCongLuong = () => {
    const navigate = useNavigate();
    const [periods, setPeriods] = useState(null);
    const [templateNames, setTemplateNames] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('ALL');
    const [month, setMonth] = useState('ALL');
    const [year, setYear] = useState('ALL');
    const [dialog, setDialog] = useState(null);
    const canView = canViewPayrollPage(PAYROLL_PATHS.periods);
    const canManage = canManagePayroll();
    const user = currentPayrollUser().username;

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            const [periodList, templates] = await Promise.all([listPeriods(), listTemplates()]);
            setPeriods(periodList);
            setTemplateNames(new Map(templates.map((template) => [template.MA_MAU, template.TEN_MAU])));
        } catch (error) {
            console.error('Lỗi tải kỳ lương:', error);
            toast.error('Không tải được danh sách kỳ lương');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    const years = useMemo(() => [...new Set((periods ?? []).map((period) => period.NAM))].sort((a, b) => b - a), [periods]);
    const templateSummary = useCallback((period) => (period.MAU_IDS ?? []).map((id) => templateNames.get(id) || id).join(', '), [templateNames]);
    const filtered = useMemo(() => (periods ?? []).filter((period) => (status === 'ALL' || period.TRANG_THAI === status)
        && (month === 'ALL' || period.THANG === Number(month))
        && (year === 'ALL' || period.NAM === Number(year))
        && matchesSearch(`${period.MA_KY} ${templateSummary(period)} ${period.NGUOI_TAO}`, search)), [periods, status, month, year, search, templateSummary]);
    const accessors = useMemo(() => ({
        period: (row) => row.NAM * 100 + row.THANG, gross: (row) => row.TONG_LUONG ?? 0, net: (row) => row.TONG_THUC_CHI ?? 0, status: (row) => row.TRANG_THAI,
    }), []);
    const { sorted, sort, toggleSort } = useSortedRows(filtered, accessors, { key: 'period', direction: 'desc' });
    const { pageRows, pagination } = usePagination(sorted);

    if (!canView) return <AccessDenied />;

    const all = periods ?? [];
    const count = (value) => all.filter((period) => period.TRANG_THAI === value).length;
    const approvedNet = all.filter((period) => period.TRANG_THAI === 'DA_DUYET').reduce((sum, period) => sum + (period.TONG_THUC_CHI || 0), 0);

    const runAndReload = async (action, successMessage) => {
        try {
            const result = await action();
            if (result?.requiresConfirmation) return result;
            toast.success(successMessage);
            setDialog(null);
            await reload();
            return result;
        } catch (error) {
            toast.error(error.message);
            return undefined;
        }
    };

    const handleSubmit = async (period) => {
        const result = await runAndReload(() => submitPeriod(period.MA_KY, false), 'Đã gửi kỳ lương chờ duyệt');
        if (result?.requiresConfirmation) setDialog({ type: 'confirmWarnings', period, warnings: result.warnings });
    };

    const rowActions = (period) => {
        const editable = period.TRANG_THAI === 'NHAP' || period.TRANG_THAI === 'TU_CHOI';
        return (
            <div className="flex justify-center gap-1">
                <IconAction icon={Eye} title="Mở bảng lương" tone="blue" onClick={() => navigate(`/bangcongluong/${period.MA_KY}`)} />
                {canManage && editable && <IconAction icon={Send} title="Gửi duyệt" onClick={() => handleSubmit(period)} />}
                {canManage && period.TRANG_THAI === 'CHO_DUYET' && (
                    <>
                        <IconAction icon={CheckCircle2} title={period.NGUOI_TAO === user ? 'Người lập không được tự duyệt' : 'Duyệt'} tone="green" disabled={period.NGUOI_TAO === user}
                            onClick={() => setDialog({ type: 'approve', period })} />
                        <IconAction icon={XCircle} title="Từ chối" tone="red" onClick={() => setDialog({ type: 'reject', period })} />
                    </>
                )}
                {canManage && period.TRANG_THAI === 'DA_DUYET' && <IconAction icon={RotateCcw} title="Mở lại" tone="amber" onClick={() => setDialog({ type: 'reopen', period })} />}
                <IconAction icon={Printer} title="In bảng lương" tone="gray" onClick={() => window.open(`/in/bangcongluong/${period.MA_KY}`, '_blank')} />
                {canManage && editable && <IconAction icon={Trash} title="Xóa kỳ" tone="red" onClick={() => setDialog({ type: 'delete', period })} />}
            </div>
        );
    };
    const statusBadge = (period) => (
        <div className="space-y-1">
            <Badge tone={STATUS_TONES[period.TRANG_THAI]}>{PAYROLL_STATUS_LABELS[period.TRANG_THAI] || period.TRANG_THAI}</Badge>
            {(period.TRANG_THAI === 'NHAP' || period.TRANG_THAI === 'TU_CHOI') && !period.CALCULATED_AT && <div><Badge tone="orange">Chưa chốt số</Badge></div>}
        </div>
    );

    return (
        <PageShell icon={Sheet} title="Bảng công lương" subtitle="Bảng lương theo tháng: nhập công, chốt số, gửi duyệt"
            actions={(
                <>
                    <button className={buttonSecondary} onClick={() => navigate('/huongdansudung?muc=cong-luong')}><BookOpen className="w-4 h-4" />Hướng dẫn</button>
                    <button className={buttonSecondary} onClick={reload} disabled={loading}><RefreshCw className="w-4 h-4" />Làm mới</button>
                    {canManage && <button className={buttonPrimary} onClick={() => navigate('/bangcongluong/tao-moi')}><Plus className="w-4 h-4" />Tạo bảng lương</button>}
                </>
            )}>
            <StatCards active={status} onSelect={setStatus} cards={[
                { key: 'total', label: 'Tổng số kỳ', value: all.length, tone: 'indigo', filterValue: 'ALL' },
                { key: 'draft', label: 'Kỳ nháp', value: count('NHAP'), tone: 'yellow', caption: count('TU_CHOI') ? `và ${count('TU_CHOI')} kỳ bị từ chối` : undefined, filterValue: 'NHAP' },
                { key: 'review', label: 'Chờ duyệt', value: count('CHO_DUYET'), tone: 'purple', filterValue: 'CHO_DUYET' },
                { key: 'approved', label: 'Đã duyệt', value: count('DA_DUYET'), tone: 'green', caption: `${formatMoney(approvedNet)} đ đã chi`, filterValue: 'DA_DUYET' },
            ]} />
            <ListToolbar search={search} onSearch={setSearch} placeholder="Tìm mã kỳ, mẫu áp dụng, người lập..."
                filterCount={[status, month, year].filter((value) => value !== 'ALL').length}
                onClearFilters={() => { setStatus('ALL'); setMonth('ALL'); setYear('ALL'); setSearch(''); }}
                filters={(
                    <>
                        <FilterField label="Tháng">
                            <select className={inputClass} value={month} onChange={(e) => setMonth(e.target.value)}>
                                <option value="ALL">Tất cả tháng</option>
                                {Array.from({ length: 12 }, (_, index) => <option key={index + 1} value={index + 1}>Tháng {index + 1}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Năm">
                            <select className={inputClass} value={year} onChange={(e) => setYear(e.target.value)}>
                                <option value="ALL">Tất cả năm</option>
                                {years.map((item) => <option key={item} value={item}>{item}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Trạng thái">
                            <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="ALL">Tất cả trạng thái</option>
                                {Object.entries(PAYROLL_STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                        </FilterField>
                    </>
                )} />
            {loading || !periods ? <Spinner /> : sorted.length === 0 ? (
                <EmptyState icon={Sheet} title="Chưa có bảng lương phù hợp">Tạo bảng lương tháng để hệ thống lập đúng danh sách nhân sự của từng mẫu.</EmptyState>
            ) : (
                <>
                    <div className="space-y-3 md:hidden">
                        {pageRows.map((period) => (
                            <div key={period.MA_KY} className="border border-gray-200 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between items-start gap-2">
                                    <Link to={`/bangcongluong/${period.MA_KY}`} className="font-semibold text-gray-900">Tháng {period.THANG}/{period.NAM}<div className="text-xs text-gray-500 font-normal">{period.MA_KY}</div></Link>
                                    {statusBadge(period)}
                                </div>
                                <p className="text-sm text-gray-700">{templateSummary(period)}</p>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div><p className="text-xs text-gray-500">Tổng cộng</p><p>{formatMoney(period.TONG_LUONG)} đ</p></div>
                                    <div><p className="text-xs text-gray-500">Thực chi</p><p className="font-semibold">{formatMoney(period.TONG_THUC_CHI)} đ</p></div>
                                </div>
                                {rowActions(period)}
                            </div>
                        ))}
                    </div>
                    <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
                        <table className={tableClass}>
                            <thead className={theadClass}>
                                <tr>
                                    <SortableTh label="Kỳ lương" sortKey="period" sort={sort} onSort={toggleSort} />
                                    <th className={thClass}>Mẫu áp dụng</th>
                                    <th className={thClass}>Người lập</th>
                                    <SortableTh label="Tổng cộng" sortKey="gross" sort={sort} onSort={toggleSort} align="right" />
                                    <th className={`${thClass} text-right`}>Ứng / BHXH</th>
                                    <SortableTh label="Thực chi" sortKey="net" sort={sort} onSort={toggleSort} align="right" />
                                    <SortableTh label="Trạng thái" sortKey="status" sort={sort} onSort={toggleSort} />
                                    <th className={`${thClass} text-center`}>Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className={tbodyClass}>
                                {pageRows.map((period) => (
                                    <tr key={period.MA_KY} className="hover:bg-gray-50 transition-colors">
                                        <td className={tdClass}>
                                            <Link to={`/bangcongluong/${period.MA_KY}`} className="flex items-center gap-1 font-semibold text-gray-900 hover:text-indigo-600">
                                                Tháng {period.THANG}/{period.NAM}<ChevronRight className="w-4 h-4" />
                                            </Link>
                                            <div className="text-xs text-gray-500">{period.MA_KY}{period.CALCULATED_AT ? ` · chốt ${formatDateTime(period.CALCULATED_AT)}` : ''}</div>
                                        </td>
                                        <td className={tdClass}><div>{templateSummary(period)}</div><div className="text-xs text-gray-500">{(period.MAU_IDS ?? []).length} mẫu áp dụng</div></td>
                                        <td className={tdClass}>{period.NGUOI_TAO}{period.NGUOI_DUYET ? <div className="text-xs text-gray-500">Duyệt: {period.NGUOI_DUYET}</div> : null}</td>
                                        <td className={`${tdClass} text-right whitespace-nowrap`}>{formatMoney(period.TONG_LUONG)} đ</td>
                                        <td className={`${tdClass} text-right whitespace-nowrap`}><div>{formatMoney(period.TONG_UNG)} đ</div><div className="text-xs text-gray-500">{formatMoney(period.TONG_BHXH)} đ</div></td>
                                        <td className={`${tdClass} text-right whitespace-nowrap font-semibold text-gray-900`}>{formatMoney(period.TONG_THUC_CHI)} đ</td>
                                        <td className={tdClass}>{statusBadge(period)}</td>
                                        <td className={tdClass}>{rowActions(period)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination pagination={pagination} unit="kỳ" />
                </>
            )}
            {dialog?.type === 'confirmWarnings' && (
                <ConfirmWarningsModal warnings={dialog.warnings} onClose={() => setDialog(null)}
                    onConfirm={() => runAndReload(() => submitPeriod(dialog.period.MA_KY, true), 'Đã gửi kỳ lương chờ duyệt')} />
            )}
            {dialog?.type === 'approve' && (
                <ConfirmDialog title="Duyệt kỳ lương" danger={false} confirmLabel="Duyệt" onClose={() => setDialog(null)}
                    message={<>Duyệt kỳ lương <b>{dialog.period.MA_KY}</b>? Phiếu ứng của kỳ sẽ chuyển sang đã khấu trừ và các mẫu đã dùng bị khóa.</>}
                    onConfirm={() => runAndReload(() => approvePeriod(dialog.period.MA_KY), 'Đã duyệt kỳ lương')} />
            )}
            {dialog?.type === 'reject' && (
                <ReasonModal title="Từ chối kỳ lương" label="Lý do từ chối" confirmLabel="Từ chối" onClose={() => setDialog(null)}
                    onSubmit={(reason) => runAndReload(() => rejectPeriod(dialog.period.MA_KY, reason), 'Đã từ chối kỳ lương')} />
            )}
            {dialog?.type === 'reopen' && (
                <ReasonModal title="Mở lại kỳ lương" label="Lý do mở lại (phiếu ứng được trả lại, số đã chốt bị xóa)" confirmLabel="Mở lại" onClose={() => setDialog(null)}
                    onSubmit={(reason) => runAndReload(() => reopenPeriod(dialog.period.MA_KY, reason), 'Đã mở lại kỳ lương')} />
            )}
            {dialog?.type === 'delete' && (
                <ConfirmDialog title="Xác nhận xóa kỳ lương" confirmLabel="Xóa kỳ" confirmText={dialog.period.MA_KY} onClose={() => setDialog(null)}
                    message="Xóa kỳ lương và toàn bộ số đã nhập. Chỉ kỳ nháp hoặc bị từ chối mới xóa được."
                    onConfirm={() => runAndReload(() => deletePeriod(dialog.period.MA_KY), 'Đã xóa kỳ lương')} />
            )}
        </PageShell>
    );
};

export default BangCongLuong;
